import React, { forwardRef, useState } from 'react';
import { TaskCard } from 'shared/components/connected/task-card';
import { SortableContainer } from 'shared/components/ui/sortable-container';
import { DateRange } from 'shared/types/date-range';
import { DraggableOptions } from 'shared/types/draggable-types';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { TaskCardTask } from 'shared/types/task-card-task';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './tasks-list.style';

type TasksListItemProps = {
  task: Task;
  dndId?: ID;
  displayDate?: Timestamp;
  displayDateRange?: DateRange;
  onTask?: (id?: ID) => void;
  onUnfocus?: (id: ID) => void;
  displayDue?: DueDisplayOptions[];
  selectedTask?: ID;
  showRepeatingAsTodo?: boolean;
  showRepeatingAsDoneOnDateEntry?: boolean;
  hideGoal?: boolean;
  hideLifeArea?: boolean;
  showDetailOnSelect?: boolean;
  tasksDisabled?: boolean;
  hasDnd?: boolean;
};

const TaskListItem: React.FC<TasksListItemProps> = ({
  task,
  dndId,
  displayDate,
  displayDateRange,
  onTask,
  onUnfocus,
  displayDue,
  selectedTask,
  showRepeatingAsTodo,
  showRepeatingAsDoneOnDateEntry,
  hideGoal,
  hideLifeArea,
  showDetailOnSelect,
  tasksDisabled,
  hasDnd,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const TaskContainer =
    hasDnd && !tasksDisabled && !task.frozenAt
      ? SortableContainer
      : React.Fragment;
  const taskContainerProps =
    hasDnd && !tasksDisabled && !task.frozenAt
      ? {
          id: dndId ?? task.id,
          type: DraggableOptions.Task,
          isEditing,
        }
      : {};

  return (
    // @ts-ignore
    <TaskContainer {...taskContainerProps}>
      <TaskCard
        task={task}
        displayDate={displayDate}
        displayDateRange={displayDateRange}
        onUnfocus={onUnfocus}
        onClick={onTask}
        onEditChange={setIsEditing}
        displayDue={displayDue}
        showRepeatingAsTodo={showRepeatingAsTodo}
        showRepeatingAsDoneOnDateEntry={showRepeatingAsDoneOnDateEntry}
        isSelected={selectedTask === task.id}
        hideGoal={hideGoal}
        hideLifeArea={hideLifeArea}
        showDetailOnSelect={showDetailOnSelect}
        isDisabled={tasksDisabled}
      />
    </TaskContainer>
  );
};

export type TasksListViewProps = {
  tasks: TaskCardTask[];
  displayDate?: Timestamp;
  displayDateRange?: DateRange;
  onTask?: (id?: ID) => void;
  onUnfocus?: (id: ID) => void;
  selectedTask?: ID;
  showRepeatingAsTodo?: boolean;
  showRepeatingAsDoneOnDateEntry?: boolean;
  displayDue?: DueDisplayOptions[];
  hideGoal?: boolean;
  hideLifeArea?: boolean;
  showDetailOnSelect?: boolean;
  tasksDisabled?: boolean;
  hasDnd?: boolean;
};

export const TasksListView = forwardRef<HTMLUListElement, TasksListViewProps>(
  (
    {
      tasks,
      displayDate,
      displayDateRange,
      onTask,
      onUnfocus,
      selectedTask,
      showRepeatingAsTodo,
      showRepeatingAsDoneOnDateEntry,
      displayDue,
      hideGoal,
      hideLifeArea,
      showDetailOnSelect,
      tasksDisabled,
      hasDnd,
    },
    ref,
  ) => (
    <Styled.List ref={ref}>
      {tasks.map((task) => (
        <Styled.ListItem key={task.id}>
          <TaskListItem
            task={task.raw.task}
            dndId={task.id}
            displayDate={displayDate}
            displayDateRange={displayDateRange}
            onUnfocus={onUnfocus}
            onTask={onTask}
            showRepeatingAsTodo={showRepeatingAsTodo}
            showRepeatingAsDoneOnDateEntry={showRepeatingAsDoneOnDateEntry}
            selectedTask={selectedTask}
            displayDue={displayDue}
            hideGoal={hideGoal}
            hideLifeArea={hideLifeArea}
            showDetailOnSelect={showDetailOnSelect}
            hasDnd={hasDnd}
            tasksDisabled={tasksDisabled}
          />
        </Styled.ListItem>
      ))}
    </Styled.List>
  ),
);
